/**
 * Zustand Auth-Store — kapselt TokenStore + AuthUser + HTTP-Client + Api in einem Singleton.
 * TokenStore, HTTP-Client und Api dürfen NICHT je Render neu entstehen (sonst läuft die
 * Single-Flight-Refresh-Pipeline kaputt), und die HTTP-Hooks brauchen Zugriff außerhalb von React.
 *
 * Ein Tab führt höchstens zwei Sitzungen: die Heimatsitzung (Refresh-Cookie, zwischen Tabs
 * geteilt) und eine Support-Sitzung, deren Token nur im Speicher dieses Tabs liegt. `user` ist
 * die wirksame Identität; jeder Wechsel meldet sich über `onIdentityChange`, bevor er sichtbar wird.
 */
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { createTokenStore, type Session } from '@stayon/auth-core';
import { buildApi, createHttpClient, HOME_SESSION_CONTEXT } from '@/shared/api';
import { apiBaseUrl as API_BASE_URL } from '@/shared/api/apiBaseUrl';
import { boundTo } from '@/shared/api/requestIdentity';
import type { AuthState } from './authState';
import { signOutAtServer } from './cookieRefresh';
import { createHydration } from './hydration';
import {
  createIdentityPublisher,
  homeKeyOf,
  memoizedApi,
  memoizedUser,
  type IdentityGate,
} from './identityPublisher';
import { createRefreshCoordinator } from './refreshCoordinator';
import { showSupportNotice } from './support/supportNotices';
import { createSupportSession } from './support/supportSession';

export type { AuthState, AuthStatus } from './authState';

type AuthStore = UseBoundStore<StoreApi<AuthState>>;

let _store: AuthStore | null = null;

export function createAuthStore(): AuthStore {
  if (_store) return _store;

  const tokenStore = createTokenStore();
  const refreshCoordinator = createRefreshCoordinator({ tokenStore });
  const homeUserOf = memoizedUser();
  const homeUser = () => homeUserOf(tokenStore.get()?.accessToken);
  // Solange nach einem Neuladen offen ist, ob der Tab in einer Support-Sitzung arbeitet, zeigt er
  // keine Identität — sonst blitzte kurz der eigene Workspace auf.
  const gate: IdentityGate = { hold: null, restoring: false };

  // `homeApi` und `publish` entstehen erst danach: der HTTP-Client braucht die Tokens der Sitzung.
  const support = createSupportSession({
    api: () => homeApi.supportSessions,
    homeKey: () => homeKeyOf(homeUser()),
    onChange: () => publish(),
    notify: (notice) =>
      showSupportNotice({ ...notice, homeWorkspaceName: homeUser()?.workspaceName ?? null }),
  });
  const http = createHttpClient({
    baseUrl: API_BASE_URL,
    tokenStore,
    refreshCoordinator,
    onRefreshState: (status) => {
      if (!gate.hold) _store?.setState({ status });
    },
    support: support.tokens,
  });
  const homeApi = buildApi(http.extend({ context: HOME_SESSION_CONTEXT }));
  const apiFor = memoizedApi((identity) => buildApi(http.extend({ context: boundTo(identity) })));
  const publish = createIdentityPublisher({ store: () => _store, homeUser, support, gate, apiFor });

  // Eine neue Anmeldung übernimmt nie die Support-Sitzung einer früheren: die endet vorher mit der
  // Heimatsitzung, die sie begonnen hat und die sie als einzige beenden kann.
  async function signIn(session: Session) {
    await support.close();
    refreshCoordinator.publishLogin(session);
  }

  const { hydrate, continueWithoutSupport } = createHydration({
    api: homeApi,
    tokenStore,
    refreshCoordinator,
    support,
    gate,
    publish,
    setState: (state) => _store?.setState(state),
  });

  _store = create<AuthState>(() => ({
    user: null,
    homeUser: null,
    support: null,
    supportResumeProblem: null,
    status: 'idle', // bis hydrate() beim Boot die Cookie-Session auflöst
    api: apiFor(''),
    homeApi,
    tokenStore,
    accessToken: () => support.token() ?? tokenStore.get()?.accessToken,

    login: async (tenantSlug, email, password) => {
      const outcome = await homeApi.auth.login({ tenantSlug, email, password });
      if (outcome.status === 'success') await signIn(outcome);
      return outcome;
    },
    completeTwoFactor: async (challengeToken, code, useRecoveryCode) => {
      await signIn(await homeApi.auth.completeTwoFactor({ challengeToken, code, useRecoveryCode }));
    },
    acceptSession: signIn,
    logout: () =>
      support.close(() => refreshCoordinator.runLogout(() => signOutAtServer(API_BASE_URL))),
    hydrate,
    continueWithoutSupport,
    startSupport: (request) => support.start(request),
    endSupport: () => support.end(),
  }));

  // Eine fremde Heimatsitzung (anderer Tab, anderes Konto) oder ihr Ende beendet die
  // Support-Sitzung; eine bloß rotierte Heimatsitzung lässt sie stehen.
  tokenStore.subscribe(() => {
    const home = homeUser();
    if (support.view() && !support.belongsTo(homeKeyOf(home))) {
      support.forget(home ? 'home-changed' : 'signed-out');
    }
    publish(home ? 'authenticated' : 'unauthenticated');
  });

  return _store;
}

export function useAuth(): AuthState {
  const store = createAuthStore();
  return store();
}
